// Port of ios/Sources/ViewModels/SystemMessageDerivation.swift.
//
// System posts — the notices the SERVER writes into a channel when someone joins, leaves, is
// added, or changes the header. A decision-for-decision port of Android's
// `SystemMessageDerivation`, tests included (shared/API.md §22).
//
// **Why the post's own `message` is not simply rendered.** Mattermost pre-renders these
// sentences server-side, in ENGLISH, at the moment they happen ("@paco has joined the
// channel."). Showing that verbatim puts English in a Spanish interface and gives a join the
// same weight, avatar and actions as something a colleague actually said. The webapp rebuilds
// the text from `type` + `props`; so does this.
//
// Pure, and deliberately stops at a `Notice` rather than producing a string: the wording lives
// in the string tables like every other user-facing sentence, so this decides WHAT is being said
// and the view decides how to say it. A type this does not know falls back to `other` carrying
// the server's own sentence — the honest failure: the server said something about this
// channel, and an empty row would hide it.

export type SystemNoticeKind =
  | 'joined'
  | 'left'
  | 'added'
  | 'removed'
  | 'headerSet'
  | 'headerCleared'
  | 'purposeSet'
  | 'purposeCleared'
  | 'renamed'
  | 'archived'
  | 'restored'
  /** Anything this client does not model; `Notice.detail` is the server's own text. */
  | 'other';

/**
 * `actor`: who did it, already in `@handle` form — the props carry a bare USERNAME and
 * nothing else, so this is the one place that names a person without the
 * teammate-name-display policy: `@handle` is unambiguous and never claims to be a display
 * name. `subject`: who it was done TO. `detail`: the new header/purpose/name, or the
 * fallback sentence for `other`.
 */
export interface SystemNotice {
  kind: SystemNoticeKind;
  actor: string;
  subject: string;
  detail: string;
}

function makeNotice(kind: SystemNoticeKind, fields: Partial<Omit<SystemNotice, 'kind'>> = {}): SystemNotice {
  return { kind, actor: fields.actor ?? '', subject: fields.subject ?? '', detail: fields.detail ?? '' };
}

/**
 * The ONE `system_*` type that is not a notice about the channel.
 *
 * `App.SendEphemeralPost` stamps `post.Type = "system_ephemeral"` on EVERY ephemeral post
 * before broadcasting it (read in the server's own source, not guessed). So a plugin's
 * private answer — the registry's "Acciones pendientes para hoy:" with one button per
 * pending action — arrived wearing a system type, was routed to the notice renderer, and
 * came out as a centred sentence with no avatar, no card and NO BUTTONS.
 *
 * That is the whole of "the registry plug-in doesn't work at all, it only sends a message"
 * (user, 2026-09-12, on Android; the same `else` arm exists here). It has nothing to do with
 * the payload: the plugin sends `type: button` actions, and the server log for that press
 * shows its backend answered with six of them.
 */
const typeEphemeral = 'system_ephemeral';

/** A post only this reader can see: a plugin's answer, never history. */
function isEphemeral(type: string | null | undefined): boolean {
  return type === typeEphemeral;
}

/**
 * True for a post the server wrote about the channel rather than a message someone sent.
 *
 * Ephemeral posts are excluded on purpose (see `typeEphemeral`): they are messages with
 * content, an author and interactive controls, and the only thing "system" about them is the
 * type string the server happens to stamp on them.
 */
function isSystem(type: string | null | undefined): boolean {
  if (!type) return false;
  return type.startsWith('system_') && !isEphemeral(type);
}

/** Swift's `.whitespaces` trim: spaces and tabs only, not newlines. */
function trimSpaces(s: string): string {
  return s.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '');
}

/**
 * `props` as a flat string map. Non-string values are stringified rather than dropped: a
 * silent drop would be indistinguishable from an absent key — which is what decides
 * whether a notice falls back to the server's own text. Malformed props are not worth a
 * crash: the notice falls back, exactly as an unknown type does.
 *
 * Stringification follows Swift's `"\(value)"` on a JSONSerialization value where it can: a
 * number prints as itself and a bool as "1"/"0" (it is an NSNumber there). Nested objects and
 * arrays print as JSON here, where Swift prints its own debug form — no rule reads those.
 */
function props(propsJson: string | null | undefined): Record<string, string> {
  if (propsJson == null || trimSpaces(propsJson) === '') return {};
  let json: unknown;
  try {
    json = JSON.parse(propsJson);
  } catch {
    return {};
  }
  if (typeof json !== 'object' || json === null || Array.isArray(json)) return {};
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(json as Record<string, unknown>)) {
    if (value === null) continue;
    if (typeof value === 'string') result[key] = value;
    else if (typeof value === 'boolean') result[key] = value ? '1' : '0';
    else if (typeof value === 'number') result[key] = String(value);
    else result[key] = JSON.stringify(value);
  }
  return result;
}

/**
 * The notice `type`/`propsJson` describe, or null when this is not a system post at all.
 * Every branch that needs a name checks it is actually there: an older server, or a type
 * whose props changed shape, must fall through to `other` rather than render "se unió al
 * canal" with nobody in front of it.
 */
function notice(
  type: string | null | undefined,
  propsJson: string | null | undefined,
  fallbackMessage: string,
): SystemNotice | null {
  if (!isSystem(type)) return null;
  const p = props(propsJson);
  const fallback = makeNotice('other', { detail: fallbackMessage });
  const username = p.username ?? '';
  const actor = username === '' ? '' : '@' + username;

  switch (type) {
    case 'system_join_channel':
    case 'system_join_team':
      return actor === '' ? fallback : makeNotice('joined', { actor });

    case 'system_leave_channel':
    case 'system_leave_team':
      return actor === '' ? fallback : makeNotice('left', { actor });

    case 'system_add_to_channel':
    case 'system_add_to_team': {
      const added = p.addedUsername ?? '';
      if (actor === '' || added === '') return fallback;
      return makeNotice('added', { actor, subject: '@' + added });
    }

    case 'system_remove_from_channel':
    case 'system_remove_from_team': {
      // The REMOVED person is the subject and the only name the props reliably carry —
      // Mattermost's own sentence is passive here too ("was removed from the channel").
      const removed = p.removedUsername ?? '';
      return removed === '' ? fallback : makeNotice('removed', { subject: '@' + removed });
    }

    case 'system_header_change': {
      const header = trimSpaces(p.new_header ?? '');
      if (actor === '') return fallback;
      if (header === '') return makeNotice('headerCleared', { actor });
      return makeNotice('headerSet', { actor, detail: header });
    }

    case 'system_purpose_change': {
      const purpose = trimSpaces(p.new_purpose ?? '');
      if (actor === '') return fallback;
      if (purpose === '') return makeNotice('purposeCleared', { actor });
      return makeNotice('purposeSet', { actor, detail: purpose });
    }

    case 'system_displayname_change': {
      const name = trimSpaces(p.new_displayname ?? '');
      if (actor === '' || name === '') return fallback;
      return makeNotice('renamed', { actor, detail: name });
    }

    // These two carry no props worth reading: the channel they are in IS the subject.
    case 'system_channel_deleted':
      return makeNotice('archived', { actor });
    case 'system_channel_restored':
      return makeNotice('restored', { actor });

    default:
      return fallback;
  }
}

export const SystemMessageDerivation = {
  typeEphemeral,
  isEphemeral,
  isSystem,
  props,
  notice,
} as const;
